import type { Exposure } from "@amplitude/experiment-js-client"
import { request } from "@prismicio/editor-support/Http"
import { captureException } from "@sentry/react"
import { unknown } from "zod"

import { getRepositoryName } from "./routing"
import { baseUrl } from "./services/core"

async function trackEvent(name: string, traits: Record<string, string>) {
  const body = {
    name,
    source: "Page Builder",
    traits,
    groupId: getRepositoryName()
  }

  return request(new URL("tracking/trace", baseUrl), {
    method: "POST",
    body,
    keepalive: true,
    okType: unknown()
  }).catch(captureException)
}

export function trackExperimentExposure(exposure: Exposure) {
  const { metadata: _, ...traits } = exposure
  void trackEvent("$exposure", traits)
}

export type SliceTrackingArgs = {
  id: string
  variation: string
  zone: string
  customtypeId: string
  sliceName: string
  documentName: string
  type: "shared" | "composite" | "simple"
}

type SliceAddedArgs = SliceTrackingArgs & {
  promptId?: string
}

export function trackAddSlice(args: SliceAddedArgs) {
  void trackEvent("Slice Added", args)
}

export function trackDeleteSlice(args: SliceTrackingArgs) {
  void trackEvent("Slice Deleted", args)
}

type SliceCopyPasteArgs = SliceTrackingArgs & {
  keyboard: boolean
}

export function trackCopyPasteSlice(
  action: "copy" | "paste",
  args: SliceCopyPasteArgs
) {
  const { keyboard } = args
  const eventName = action === "copy" ? "Slice Copied" : "Slice Pasted"
  void trackEvent(eventName, { ...args, keyboard: `${keyboard}` })
}

type SliceVariationChangedArgs = SliceTrackingArgs & {
  variationName: string
}

export function trackSliceVariationChanged(args: SliceVariationChangedArgs) {
  void trackEvent("Slice Variation Changed", args)
}

export function trackSimulatorUrlSetup(args: { url: string }) {
  void trackEvent("Simulator URL Setup", args)
}

export function trackPreviewOpen(args: { documentId: string; label: string }) {
  void trackEvent("Open Preview", args)
}

export function trackSlicePromptedStarted(args: {
  customTypeId: string
  documentId?: string
  prompt: string
  promptId: string
  locale: string
}) {
  void trackEvent("Slice Prompted Started", args)
}

export function trackSlicePromptedDone(args: {
  customTypeId: string
  documentId?: string
  prompt: string
  promptId: string
  locale: string
  slices: string
  cost: string
  time: string
}) {
  void trackEvent("Slice Prompted Done", args)
}

export function trackRefineSliceStarted(args: {
  customTypeId: string
  documentId?: string
  prompt: string
  promptId: string
  locale: string
}) {
  void trackEvent("Refine Slice Started", args)
}

export function trackRefineSliceDone(args: {
  customTypeId: string
  documentId?: string
  prompt: string
  promptId: string
  locale: string
  cost: string
  time: string
}) {
  void trackEvent("Refine Slice Done", args)
}

export function trackSmartCopyPasteStarted(args: {
  customTypeId: string
  documentId?: string
  sliceId: string
  variationId: string
  retried?: "true"
}) {
  void trackEvent("Smart Copy Paste Started", args)
}

export function trackSmartCopyPasteDone(args: {
  customTypeId: string
  documentId?: string
  sliceId: string
  variationId: string
  retried?: "true"
  cost: string
  time: string
  distance: string
}) {
  void trackEvent("Smart Copy Paste Done", args)
}

export function trackTranslateDocumentStarted(args: {
  customTypeId: string
  documentId?: string
  sourceLocale: string
  targetLocale: string
  promptId: string
}) {
  void trackEvent("Translate Document Started", args)
}

export function trackTranslateDocumentDone(args: {
  customTypeId: string
  documentId?: string
  sourceLocale: string
  targetLocale: string
  promptId: string
  model: string
  sourceTokens: string
  targetTokens: string
  tokenRatio: string
  cost: string
  time: string
}) {
  void trackEvent("Translate Document Done", args)
}

export function trackDocumentCopiedToLocale(args: {
  customTypeId: string
  documentId?: string
  languageFrom: string
  languageTo: string
}) {
  void trackEvent("Document Copied To Locale", args)
}

export function trackLivePreviewModalOpen(args: {
  documentId: string
  customTypeId: string
  repositoryId: string
}) {
  void trackEvent("Live Preview Modal Open", args)
}

export function trackWhatsNewModalOpen(args: {
  documentId: string
  customTypeId: string
  repositoryId: string
}) {
  void trackEvent("Whats New Modal Open", args)
}

export function trackEditorEvent(
  event: string,
  args: Record<string, string> = {}
) {
  void trackEvent(event, args)
}

export function trackDocumentWithLiveEditingPreview(args: {
  documentId: string
  customTypeId: string
  repositoryId: string
  preview: "true" | "false"
  error?: string
}) {
  void trackEvent("Document With Live Editing Preview", args)
}
